import { useState, useEffect, useCallback } from 'react';
import { fetchMarketingLeads, extractApiError } from '../services/marketingApi';
import type {
  MarketingInterest,
  MarketingLead,
  MarketingLeadsResponse,
} from '../types';

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface UseMarketingLeadsReturn {
  data: MarketingLeadsResponse | null;
  total: number;
  loading: boolean;
  error: string | null;
  // filters
  startDate: string;
  endDate: string;
  interest: MarketingInterest;
  includeActioned: boolean;
  stage: string;
  limit: number;
  offset: number;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  setInterest: (value: MarketingInterest) => void;
  setIncludeActioned: (value: boolean) => void;
  setStage: (value: string) => void;
  setLimit: (value: number) => void;
  setOffset: (value: number) => void;
  refetch: () => Promise<void>;
  /** Replace a single lead in place after a write (optimistic-friendly). */
  updateLead: (lead: MarketingLead) => void;
}

/**
 * Work-queue hook for marketing leads. Fetching is gated by `enabled`
 * so the loans/marketing views only hit the API when they are active.
 */
export function useMarketingLeads(enabled: boolean): UseMarketingLeadsReturn {
  const today = formatDate(new Date());
  const [startDate, setStartDateRaw] = useState<string>(today);
  const [endDate, setEndDateRaw] = useState<string>(today);
  const [interest, setInterestRaw] = useState<MarketingInterest>('interested');
  const [includeActioned, setIncludeActionedRaw] = useState<boolean>(false);
  const [stage, setStageRaw] = useState<string>(''); // '' = all products
  const [limit, setLimitRaw] = useState<number>(50);
  const [offset, setOffset] = useState<number>(0);

  const [data, setData] = useState<MarketingLeadsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Any filter change should send the pager back to the first page.
  const setStartDate = useCallback((v: string) => {
    setStartDateRaw(v);
    setOffset(0);
  }, []);
  const setEndDate = useCallback((v: string) => {
    setEndDateRaw(v);
    setOffset(0);
  }, []);
  const setInterest = useCallback((v: MarketingInterest) => {
    setInterestRaw(v);
    setOffset(0);
  }, []);
  const setIncludeActioned = useCallback((v: boolean) => {
    setIncludeActionedRaw(v);
    setOffset(0);
  }, []);
  const setStage = useCallback((v: string) => {
    setStageRaw(v);
    setOffset(0);
  }, []);
  const setLimit = useCallback((v: number) => {
    setLimitRaw(v);
    setOffset(0);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMarketingLeads({
        interest,
        include_actioned: includeActioned,
        stage,
        start_date: startDate,
        end_date: endDate,
        limit,
        offset,
      });
      setData(result);
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [interest, includeActioned, stage, startDate, endDate, limit, offset]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled, fetchData]);

  const updateLead = useCallback((lead: MarketingLead) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        leads: prev.leads.map((l) => (l.call_uuid === lead.call_uuid ? lead : l)),
      };
    });
  }, []);

  return {
    data,
    total: data?.total ?? 0,
    loading,
    error,
    startDate,
    endDate,
    interest,
    includeActioned,
    stage,
    limit,
    offset,
    setStartDate,
    setEndDate,
    setInterest,
    setIncludeActioned,
    setStage,
    setLimit,
    setOffset,
    refetch: fetchData,
    updateLead,
  };
}
